import { Button, Spinner } from '@heroui/react'
import { CheckoutSessionStatusResponseDto, useGetCheckoutSessionStatus } from '@qse/api'
import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { LogoIcon } from '@qse/ui'
import { UseQueryOptions } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { AlertCircle, ArrowRight, RefreshCw, Settings, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AppRoutesList } from '@/router/routes'

const Success = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const sessionId = searchParams.get('session_id')
  const [isVisible, setIsVisible] = useState(false)
  const { user } = useUserStore()

  const {
    data: session,
    isLoading,
    error,
  } = useGetCheckoutSessionStatus(sessionId || '', {
    enabled: user?.userRole !== UserRoleEnum.TENANT_USER,
  } as UseQueryOptions<CheckoutSessionStatusResponseDto, Error>)

  useEffect(() => {
    // Trigger animations on component mount
    const timer = setTimeout(() => setIsVisible(true), 100)

    return () => clearTimeout(timer)
  }, [])

  const handleGoToDashboard = () => {
    navigate(AppRoutesList.users)
  }

  const handleGoToSubscription = () => {
    navigate(AppRoutesList.managementSubscription)
  }

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900/20 flex items-center justify-center p-4">
        <div className="relative">
          <div>
            <div className="text-center">
              <Spinner className="mx-auto mb-4" color="primary" size="lg" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Loading Payment Details
              </h2>

              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Please wait while we verify your payment...
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-rose-50 dark:from-gray-900 dark:via-gray-800 dark:to-red-900/20 flex items-center justify-center p-4">
        <div className="relative">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="relative z-10 p-8 max-w-md"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center">
              {/* Error Icon */}
              <motion.div
                animate={{ scale: 1 }}
                className="w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center"
                initial={{ scale: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <AlertCircle className="w-8 h-8 text-red-500" />
              </motion.div>

              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Oops! Something went wrong
              </h2>

              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We couldn't retrieve your payment details. Please try again or contact support if
                the problem persists.
              </p>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold"
                  color="primary"
                  size="lg"
                  startContent={<RefreshCw className="w-4 h-4" />}
                  variant="shadow"
                  onPress={() => window.location.reload()}
                >
                  Try Again
                </Button>

                <Button
                  className="w-full text-gray-600 dark:text-gray-300"
                  size="lg"
                  startContent={<ArrowRight className="w-4 h-4" />}
                  variant="light"
                  onPress={handleGoToDashboard}
                >
                  Go to Dashboard
                </Button>
              </div>

              {/* Error Details */}
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/50">
                <p className="text-xs text-red-600 dark:text-[#DD524C]">
                  Error: {(error as any)?.message || 'Failed to load session data'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  // No session data
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-yellow-900/20 flex items-center justify-center p-4">
        <div className="relative">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={{ scale: 1 }}
              className="w-16 h-16 mx-auto mb-6 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center"
              initial={{ scale: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <AlertCircle className="w-8 h-8 text-yellow-500" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              No Payment Found
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We couldn't find any payment session. Please check your URL or contact support.
            </p>
            <Button
              className="w-full"
              color="primary"
              endContent={<ArrowRight className="w-4 h-4" />}
              size="lg"
              variant="shadow"
              onPress={handleGoToDashboard}
            >
              Go to Dashboard
            </Button>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br h-screen relative bg-white dark:bg-black flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden h-full">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-300/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-300/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        animate={isVisible ? 'visible' : 'hidden'}
        className="relative z-10 max-w-md w-full"
        initial="hidden"
        variants={{
          hidden: { opacity: 0, y: 50 },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.43, 0.13, 0.23, 0.96],
              staggerChildren: 0.2,
            },
          },
        }}
      >
        <motion.div
          className="text-center mb-8"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.5,
                ease: [0.43, 0.13, 0.23, 0.96],
              },
            },
          }}
        >
          <motion.div
            animate={{ scale: 1 }}
            className="relative mx-auto w-32 h-32 mb-4"
            initial={{ scale: 0 }}
            transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200,
              damping: 10,
            }}
          >
            <motion.div
              className="absolute inset-0 dark:bg-white bg-black rounded-full"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute inset-0 dark:bg-white bg-black rounded-full"
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.3, 0, 0.3],
              }}
              transition={{
                repeat: Infinity,
                duration: 2,
                delay: 0.5,
                ease: 'easeInOut',
              }}
            />

            <div className="relative z-10 w-full h-full dark:bg-white bg-black rounded-full flex items-center justify-center shadow-xl">
              <LogoIcon className=" text-white dark:text-black" fill="currentColor" />
            </div>
          </motion.div>

          {/* Floating sparkles */}
          <motion.div
            className="absolute top-4 right-1/4"
            animate={{
              y: [-10, 10, -10],
              rotate: [0, 180, 360],
            }}
            transition={{
              repeat: Infinity,
              duration: 3,
              ease: 'easeInOut',
            }}
          >
            <Sparkles className="w-6 h-6 text-yellow-400" />
          </motion.div>
          <motion.div
            className="absolute top-8 left-1/4"
            animate={{
              y: [10, -10, 10],
              rotate: [360, 180, 0],
            }}
            transition={{
              repeat: Infinity,
              duration: 4,
              ease: 'easeInOut',
            }}
          >
            <Sparkles className="w-4 h-4 text-pink-400" />
          </motion.div>
        </motion.div>

        {/* Success Message */}
        <motion.div
          animate="visible"
          className="text-center mb-8"
          initial="hidden"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: [0.43, 0.13, 0.23, 0.96],
              },
            },
          }}
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Payment Successful! 🎉
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-2">
            Thank you for your purchase
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your subscription has been activated successfully
          </p>
        </motion.div>

        {/* Action Buttons */}

        <div className="flex flex-col gap-4">
          <motion.div
            animate="visible"
            className="space-y-4"
            initial="hidden"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.43, 0.13, 0.23, 0.96],
                },
              },
            }}
          >
            <Button
              className="w-full font-semibold transition-all duration-300 transform hover:scale-105"
              color="primary"
              endContent={<Settings className="w-5 h-5" />}
              radius="sm"
              size="lg"
              variant="shadow"
              onPress={handleGoToSubscription}
            >
              Manage Subscription
            </Button>
          </motion.div>
          <motion.div
            animate="visible"
            className="space-y-4"
            initial="hidden"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.43, 0.13, 0.23, 0.96],
                },
              },
            }}
          >
            <Button
              className="w-full font-semibold transition-all duration-300 transform hover:scale-105"
              color="primary"
              endContent={<ArrowRight className="w-5 h-5" />}
              radius="sm"
              size="lg"
              variant="shadow"
              onPress={handleGoToDashboard}
            >
              Go to Dashboard
            </Button>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          className="text-center mt-6"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: [0.43, 0.13, 0.23, 0.96],
              },
            },
          }}
        >
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Need help? Contact our support team anytime
          </p>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default Success
